import { FieldConfig } from "@/types/fieldConfig";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Chip,
    Button,
} from "@heroui/react";
import { useCallback, useMemo, useState } from "react";
import useRequest from "@/lib/request/useRequest";
import {
    ApplicationPlan,
    PlantingPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import { CropConfiguration, CropStage } from "@/types/cropConfiguration";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import { ConfirmPrimaryPlan } from "./ConfirmPlan";
import { applicationPlanColumns, plantingPlanColumns } from "./plannerColumns";
import ExpandingTable from "@/components/table/ExpandingTable";
import { OnChangeFn, RowSelectionState, Updater } from "@tanstack/react-table";

function BulkNPlanner({
    fields,
    onClose,
}: {
    fields: FieldConfig[];
    onClose?: () => void;
}) {
    const { useGetRequest } = useRequest();

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: `/crops/`,
    });

    const cropPlansRequest = useGetRequest<SimulatedCropPlan[]>({
        url: `/simulated_crop_plans/`,
    });

    const plantingPlansRequest = useGetRequest<PlantingPlan[]>({
        url: `/planting_plans/`,
    });

    const applicationPlansRequest = useGetRequest<ApplicationPlan[]>({
        url: `/application_plans/`,
    });

    const [selectedApplicationPlan, setSelectedApplicationPlan] =
        useState<ApplicationPlan | null>(null);
    const [selectedPlantingPlan, setSelectedPlantingPlan] =
        useState<PlantingPlan | null>(null);

    const cropStages = useMemo(() => {
        if (!cropsQuery.data || !selectedPlantingPlan?.crop_configuration) {
            return [];
        }
        const selectedCrop = cropsQuery.data.find(
            (crop) => crop.id === selectedPlantingPlan.crop_configuration,
        );
        if (selectedCrop) {
            return selectedCrop.stages;
        }

        return [];
    }, [cropsQuery.data, selectedPlantingPlan?.crop_configuration]);

    const [step, setStep] = useState<"planting" | "applications">("planting");

    const plantingColumns = useMemo(() => {
        return [
            plantingPlanColumns.selection(),
            plantingPlanColumns.name(),
            plantingPlanColumns.crop_configuration(
                cropsQuery?.data?.map((crop) => ({
                    id: crop.id,
                    description: crop.crop_name,
                })) ?? [],
            ),
            plantingPlanColumns.date(),
            plantingPlanColumns.seeding_rate(),
            plantingPlanColumns.seeding_depth(),
        ];
    }, [cropsQuery?.data]);

    const plantingData = useMemo(() => {
        return plantingPlansRequest?.data ?? [];
    }, [plantingPlansRequest?.data]);

    const plantingRowSelection = useMemo(() => {
        if (!selectedPlantingPlan) return {};
        return {
            [selectedPlantingPlan?.id?.toString()]: true,
        };
    }, [selectedPlantingPlan]);

    const getPlantingRowId = useCallback(
        (row: PlantingPlan) => row.id.toString(),
        [],
    );

    const onPlantingRowSelectionChange: OnChangeFn<RowSelectionState> =
        useCallback(
            (updaterOrValue: Updater<RowSelectionState>) => {
                if (typeof updaterOrValue === "function") {
                    const value = updaterOrValue(plantingRowSelection);
                    const keys = Object.keys(value);
                    if (keys.length > 0) {
                        const plantingPlan = plantingData.find(
                            (plan) => plan.id === parseInt(keys[0]),
                        );
                        if (plantingPlan) {
                            setSelectedPlantingPlan(plantingPlan);
                        } else {
                            setSelectedPlantingPlan(null);
                        }
                    } else {
                        setSelectedPlantingPlan(null);
                    }
                }
            },
            [plantingData, plantingRowSelection],
        );

    const cropStageLookup = useMemo(() => {
        return cropStages.reduce(
            (acc, cropStage) => {
                acc[cropStage.id] = cropStage;
                return acc;
            },
            {} as Record<number, CropStage>,
        );
    }, [cropStages]);

    const applicationColumns = useMemo(() => {
        return [
            applicationPlanColumns.selection(),
            applicationPlanColumns.name(),
            applicationPlanColumns.applications(cropStageLookup),
        ];
    }, [cropStageLookup]);

    const applicationData = useMemo(() => {
        return applicationPlansRequest?.data ?? [];
    }, [applicationPlansRequest?.data]);

    const applicationRowSelection = useMemo(() => {
        if (!selectedApplicationPlan) return {};
        return {
            [selectedApplicationPlan?.id?.toString()]: true,
        };
    }, [selectedApplicationPlan]);

    const getApplicationRowId = useCallback(
        (row: ApplicationPlan) => row.id.toString(),
        [],
    );

    const onApplicationRowSelectionChange: OnChangeFn<RowSelectionState> =
        useCallback(
            (updaterOrValue: Updater<RowSelectionState>) => {
                if (typeof updaterOrValue === "function") {
                    const value = updaterOrValue(applicationRowSelection);
                    const keys = Object.keys(value);
                    if (keys.length > 0) {
                        const applicationPlan = applicationData.find(
                            (plan) => plan.id === parseInt(keys[0]),
                        );
                        if (applicationPlan) {
                            setSelectedApplicationPlan(applicationPlan);
                        } else {
                            setSelectedApplicationPlan(null);
                        }
                    } else {
                        setSelectedApplicationPlan(null);
                    }
                }
            },
            [applicationData, applicationRowSelection],
        );

    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

    return (
        <>
            <Modal
                isOpen={true}
                onClose={onClose}
                className="max-w-5xl h-[90vh]"
                scrollBehavior="inside"
                isDismissable={false}
            >
                <ModalContent>
                    <ModalHeader>
                        <div className="flex flex-row items-center w-full justify-center gap-4 font-bold text-3xl text-primary-500 ml-4">
                            <img
                                src={allNHorizontalImage}
                                alt="All N Horizontal"
                                className="h-10 object-cover"
                            />
                            Assign Nitrogen Plans
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        {step === "planting" && (
                            <div>
                                <ExpandingTable
                                    columns={plantingColumns}
                                    data={plantingData}
                                    sorting={[]}
                                    setSorting={() => {}}
                                    enableMultiRowSelection={false}
                                    getRowId={getPlantingRowId}
                                    onRowSelectionChange={
                                        onPlantingRowSelectionChange
                                    }
                                    rowSelection={plantingRowSelection}
                                />
                            </div>
                        )}
                        {step === "applications" && (
                            <div>
                                <ExpandingTable
                                    columns={applicationColumns}
                                    data={applicationData}
                                    sorting={[]}
                                    setSorting={() => {}}
                                    onSelectionChange={(selectedData) => {
                                        if (selectedData.length > 0) {
                                            setSelectedApplicationPlan(
                                                selectedData[0],
                                            );
                                        }
                                    }}
                                    enableMultiRowSelection={false}
                                    getRowId={getApplicationRowId}
                                    onRowSelectionChange={
                                        onApplicationRowSelectionChange
                                    }
                                    rowSelection={applicationRowSelection}
                                />
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter className="flex flex-row justify-between">
                        <div className="flex flex-row gap-3 items-center w-full">
                            <div className="flex flex-row w-full items-center gap-1 font-semibold text-xl text-primary-500 overflow-x-auto">
                                Fields:
                                {fields.map((field) => (
                                    <Chip
                                        key={field.id}
                                        color="primary"
                                        variant="flat"
                                    >
                                        {field.name}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                        <div>
                            {step === "planting" && (
                                <Button
                                    color="primary"
                                    onPress={() => {
                                        setStep("applications");
                                    }}
                                    isDisabled={!selectedPlantingPlan}
                                >
                                    Select Application Plan
                                </Button>
                            )}
                            {step === "applications" && (
                                <div className="flex flex-row gap-2 items-center">
                                    <Button
                                        color="default"
                                        onPress={() => {
                                            setStep("planting");
                                        }}
                                    >
                                        Select Planting Plan
                                    </Button>
                                    <Button
                                        color="primary"
                                        onPress={() => {
                                            setShowConfirmation(true);
                                        }}
                                    >
                                        Assign
                                    </Button>
                                </div>
                            )}
                        </div>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {showConfirmation && (
                <ConfirmPrimaryPlan
                    fields={fields}
                    plantingPlan={selectedPlantingPlan!}
                    applicationPlan={selectedApplicationPlan!}
                    cropPlans={cropPlansRequest.data}
                    onSave={onClose}
                />
            )}
        </>
    );
}

export default BulkNPlanner;
